import React, { useState, useMemo } from 'react';
import { Portfolio, Expense, Income, InvestmentFolio } from '../types';
import { formatCurrency } from '../utils/calculations';
import { TrendingUp, DollarSign, CreditCard, BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FinancialModelProps {
  portfolio: Portfolio;
  hideHeader?: boolean;
}

interface YearProjection {
  year: number; // Year number (1, 2, 3...)
  actualYear: number; // Actual calendar year (2024, 2025, 2026...)
  monthsRemaining?: number; // For current year, months remaining
  expenses: number;
  income: number;
  netCashFlow: number;
  propertyValue: number;
  nonPropertyValue: number;
  totalInvestmentValue: number;
  cumulativeNetWorth: number;
}

const FinancialModel: React.FC<FinancialModelProps> = ({ portfolio, hideHeader = false }) => {
  const [inflationRate, setInflationRate] = useState<number>(6);
  const [investmentGrowthRate, setInvestmentGrowthRate] = useState<number>(10);
  const [projectionYears, setProjectionYears] = useState<number>(30);
  
  // Calculate default income stopping year (current year + 20 years)
  const currentYear = new Date().getFullYear();
  const [incomeStoppingYear, setIncomeStoppingYear] = useState<number>(currentYear + 20);

  // Calculate current monthly expenses (recurring expenses converted to monthly)
  const currentMonthlyExpenses = useMemo(() => {
    if (!portfolio.expenses) return 0;
    
    return portfolio.expenses
      .filter(exp => exp.isActive && exp.type === 'recurring')
      .reduce((total, exp) => {
        let monthlyAmount = exp.amount;
        if (exp.frequency === 'quarterly') {
          monthlyAmount = exp.amount / 3;
        } else if (exp.frequency === 'yearly') {
          monthlyAmount = exp.amount / 12;
        }
        return total + monthlyAmount;
      }, 0);
  }, [portfolio.expenses]);

  // Calculate current monthly income
  const currentMonthlyIncome = useMemo(() => {
    if (!portfolio.incomes) return 0;
    
    return portfolio.incomes
      .filter(inc => inc.isActive && inc.frequency !== 'one-time')
      .reduce((total, inc) => {
        let monthlyAmount = inc.amount;
        if (inc.frequency === 'quarterly') {
          monthlyAmount = inc.amount / 3;
        } else if (inc.frequency === 'yearly') {
          monthlyAmount = inc.amount / 12;
        }
        return total + monthlyAmount;
      }, 0);
  }, [portfolio.incomes]);

  // Calculate current total investment value
  const currentInvestmentValue = useMemo(() => {
    if (!portfolio.investments) return 0;
    return portfolio.investments.reduce((total, inv) => total + inv.currentValue, 0);
  }, [portfolio.investments]);

  // Calculate current property and non-property values
  const currentPropertyValue = useMemo(() => {
    if (!portfolio.investments) return 0;
    return portfolio.investments
      .filter(inv => inv.type === 'Properties')
      .reduce((total, inv) => total + inv.currentValue, 0);
  }, [portfolio.investments]);

  const currentNonPropertyValue = useMemo(() => {
    if (!portfolio.investments) return 0;
    return portfolio.investments
      .filter(inv => inv.type !== 'Properties')
      .reduce((total, inv) => total + inv.currentValue, 0);
  }, [portfolio.investments]);

  // Calculate projections for 30 years
  const projections = useMemo(() => {
    const years: YearProjection[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11
    const monthsRemaining = 12 - currentMonth;
    
    // Determine starting year: if less than 1 month left, start from next year
    const startYear = monthsRemaining < 1 ? currentYear + 1 : currentYear;
    const monthsInFirstYear = monthsRemaining < 1 ? 12 : monthsRemaining;
    
    // Track running values year over year
    let runningNonPropertyValue = currentNonPropertyValue;
    let runningPropertyValue = currentPropertyValue;
    
    for (let yearNum = 1; yearNum <= projectionYears; yearNum++) {
      const actualYear = startYear + yearNum - 1;
      const isFirstYear = yearNum === 1;
      const monthsForThisYear = isFirstYear ? monthsInFirstYear : 12;
      const monthMultiplier = monthsForThisYear / 12;
      
      // Calculate annual values (will be pro-rated for first year)
      const annualExpensesBase = currentMonthlyExpenses * 12 * Math.pow(1 + inflationRate / 100, yearNum - 1);
      const annualIncomeBase = currentMonthlyIncome * 12;
      
      // Pro-rate for first year based on months remaining
      const expenses = annualExpensesBase * monthMultiplier;
      
      // Income stops after incomeStoppingYear
      // If actualYear is greater than or equal to incomeStoppingYear, income is 0
      const income = actualYear >= incomeStoppingYear 
        ? 0 
        : annualIncomeBase * monthMultiplier;
      
      // Net cash flow (income - expenses)
      const netCashFlow = income - expenses;
      
      // Apply growth to non-property investments (pro-rated for first year)
      const growthMultiplier = Math.pow(1 + investmentGrowthRate / 100, monthMultiplier);
      runningNonPropertyValue = runningNonPropertyValue * growthMultiplier;
      
      // Then, adjust for cash flow:
      // If deficit (expenses > income), reduce non-property investments (can go negative)
      // If surplus (income > expenses), add to non-property investments
      // Allow negative values to show when wealth is depleted
      runningNonPropertyValue += netCashFlow;
      
      // Property value stays constant
      const propertyValue = runningPropertyValue;
      const nonPropertyValue = runningNonPropertyValue;
      const totalInvestmentValue = propertyValue + nonPropertyValue;
      
      // Cumulative net worth = total investment value
      const cumulativeNetWorth = totalInvestmentValue;
      
      years.push({
        year: yearNum,
        actualYear,
        monthsRemaining: isFirstYear ? monthsInFirstYear : undefined,
        expenses,
        income,
        netCashFlow,
        propertyValue,
        nonPropertyValue,
        totalInvestmentValue,
        cumulativeNetWorth
      });
    }
    
    return years;
  }, [currentMonthlyExpenses, currentMonthlyIncome, currentPropertyValue, currentNonPropertyValue, inflationRate, investmentGrowthRate, projectionYears, incomeStoppingYear]);

  return (
    <div className={hideHeader ? "space-y-6" : "p-6 space-y-6"}>
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-6 w-6 text-primary-600" />
              <h2 className="text-2xl font-bold text-gray-900">Financial Model</h2>
            </div>
            <p className="text-gray-600 mt-1">{projectionYears}-year projection of expenses, income, and investments</p>
          </div>
          
          {/* Configuration inputs */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Expense Inflation:</label>
            <input
              type="number"
              value={inflationRate}
              onChange={(e) => setInflationRate(parseFloat(e.target.value) || 0)}
              min="0"
              max="20"
              step="0.1"
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Investment Growth:</label>
            <input
              type="number"
              value={investmentGrowthRate}
              onChange={(e) => setInvestmentGrowthRate(parseFloat(e.target.value) || 0)}
              min="0"
              max="30"
              step="0.1"
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
          
          <div className="flex items-center space-x-3 border-l border-gray-300 pl-4">
            <label className="text-sm text-gray-600 whitespace-nowrap">Projection Years:</label>
            <div className="flex items-center space-x-3">
              <input
                type="range"
                value={projectionYears}
                onChange={(e) => setProjectionYears(parseInt(e.target.value))}
                min="5"
                max="50"
                step="1"
                className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <span className="text-sm font-medium text-gray-900 w-8">{projectionYears}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 border-l border-gray-300 pl-4">
            <label className="text-sm text-gray-600 whitespace-nowrap">Income Stopping Year:</label>
            <input
              type="number"
              value={incomeStoppingYear}
              onChange={(e) => setIncomeStoppingYear(parseInt(e.target.value) || currentYear)}
              min={currentYear}
              max={currentYear + 100}
              step="1"
              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
        </div>
      )}
      {hideHeader && (
        <div className="flex items-center justify-end">
          {/* Configuration inputs */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Expense Inflation:</label>
              <input
                type="number"
                value={inflationRate}
                onChange={(e) => setInflationRate(parseFloat(e.target.value) || 0)}
                min="0"
                max="20"
                step="0.1"
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Investment Growth:</label>
              <input
                type="number"
                value={investmentGrowthRate}
                onChange={(e) => setInvestmentGrowthRate(parseFloat(e.target.value) || 0)}
                min="0"
                max="30"
                step="0.1"
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            
            <div className="flex items-center space-x-3 border-l border-gray-300 pl-4">
              <label className="text-sm text-gray-600 whitespace-nowrap">Projection Years:</label>
              <div className="flex items-center space-x-3">
                <input
                  type="range"
                  value={projectionYears}
                  onChange={(e) => setProjectionYears(parseInt(e.target.value))}
                  min="5"
                  max="50"
                  step="1"
                  className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
                <span className="text-sm font-medium text-gray-900 w-8">{projectionYears}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 border-l border-gray-300 pl-4">
              <label className="text-sm text-gray-600 whitespace-nowrap">Income Stopping Year:</label>
              <input
                type="number"
                value={incomeStoppingYear}
                onChange={(e) => setIncomeStoppingYear(parseInt(e.target.value) || currentYear)}
                min={currentYear}
                max={currentYear + 100}
                step="1"
                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center space-x-2 text-gray-600 mb-1">
            <CreditCard className="h-4 w-4" />
            <span className="text-sm font-medium">Current Monthly Expenses</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(currentMonthlyExpenses)}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center space-x-2 text-gray-600 mb-1">
            <DollarSign className="h-4 w-4" />
            <span className="text-sm font-medium">Current Monthly Income</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(currentMonthlyIncome)}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center space-x-2 text-gray-600 mb-1">
            <TrendingUp className="h-4 w-4" />
            <span className="text-sm font-medium">Current Investments</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(currentInvestmentValue)}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center space-x-2 text-gray-600 mb-1">
            <BarChart3 className="h-4 w-4" />
            <span className="text-sm font-medium">{projectionYears}-Year Net Worth</span>
          </div>
          <div className={`text-2xl font-bold ${
            (projections[projections.length - 1]?.cumulativeNetWorth || 0) < 0 
              ? 'text-danger-600' 
              : 'text-primary-600'
          }`}>
            {formatCurrency(projections[projections.length - 1]?.cumulativeNetWorth || 0)}
          </div>
        </div>
      </div>

      {/* Progression Charts - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Chart */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Income vs Expenses</h3>
            <p className="text-sm text-gray-600 mt-1">Annual income and expenses over {projectionYears} years</p>
          </div>
          <div className="p-4 md:p-6">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={projections} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="actualYear" 
                label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                tickFormatter={(value) => `${value}`}
              />
                <YAxis 
                  label={{ value: 'Amount (₹)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => {
                    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
                    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
                    return `₹${(value / 1000).toFixed(0)}K`;
                  }}
                />
                <Tooltip 
                  formatter={(value) => {
                    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
                    return formatCurrency(numValue);
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="income" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Annual Income"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="expenses" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="Annual Expenses"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Wealth Accumulation Chart */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Wealth Accumulation</h3>
            <p className="text-sm text-gray-600 mt-1">Cumulative net worth over {projectionYears} years</p>
          </div>
          <div className="p-4 md:p-6">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={projections} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="actualYear" 
                label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                tickFormatter={(value) => `${value}`}
              />
                <YAxis 
                  label={{ value: 'Net Worth (₹)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => {
                    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
                    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
                    return `₹${(value / 1000).toFixed(0)}K`;
                  }}
                />
                <Tooltip 
                  formatter={(value) => {
                    const numValue = typeof value === 'number' ? value : parseFloat(String(value || 0));
                    return formatCurrency(numValue);
                  }}
                  labelFormatter={(label) => `${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="cumulativeNetWorth" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Cumulative Net Worth"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Projections Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{projectionYears}-Year Financial Projection</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Year
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Annual Expenses
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Annual Income
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Net Cash Flow
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Non-Property
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Cumulative Net Worth
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projections.map((projection) => (
                <tr key={projection.year} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {projection.actualYear}
                    {projection.monthsRemaining && projection.monthsRemaining < 12 && (
                      <span className="text-xs text-gray-500 ml-1">({projection.monthsRemaining} months)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(projection.expenses)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(projection.income)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                    projection.netCashFlow >= 0 ? 'text-success-600' : 'text-danger-600'
                  }`}>
                    {formatCurrency(projection.netCashFlow)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                    projection.nonPropertyValue < 0 ? 'text-danger-600' : 'text-gray-900'
                  }`}>
                    {formatCurrency(projection.nonPropertyValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(projection.propertyValue)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold text-right ${
                    projection.cumulativeNetWorth < 0 ? 'text-danger-600' : 'text-primary-600'
                  }`}>
                    {formatCurrency(projection.cumulativeNetWorth)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialModel;

